import { gql, useQuery } from '@apollo/client';
import Icon from 'application/components/icon';
import useTranslate from 'application/hooks/use-translate';
import moment from 'moment';
import React, { useRef, useState } from 'react';

const STRIPE_SUBSCRIPTION_PERIOD = gql`
  query SubscriptionPeriod($userId: ID!, $previousMonths: Int) {
    user(id: $userId) {
      id
      stripeSubscription {
        productName

        subscriptionPeriod(previousMonths: $previousMonths) {
          start
          end
          limit
          used
          remaining
          percentageUsed
          percentageRemaining
        }
      }
    }
  }
`;

interface SubscriptionPeriodQuery {
  user: {
    id: string;
    stripeSubscription: {
      productName: string;

      subscriptionPeriod: {
        start: Date;
        end: Date;
        limit: number | null;
        used: number;
        remaining: number | null;
        percentageUsed: number | null;
        percentageRemaining: number | null;
      };
    } | null;
  };
}

interface Props {
  userId: string;
}

const formatPeriodDate = (date: Date) => moment(date).format('LLL');

type SubscriptionPeriod = NonNullable<
  NonNullable<SubscriptionPeriodQuery['user']['stripeSubscription']>['subscriptionPeriod']
>;

const PaymentRequestUsage = ({ userId }: Props) => {
  const translate = useTranslate('pages.profile.payment-request-usage');

  const [previousMonths, setPreviousMonths] = useState(0);
  const lastDisplayedRef = useRef<{
    productName: string;
    period: SubscriptionPeriod;
  } | null>(null);

  const { data, loading } = useQuery<SubscriptionPeriodQuery>(STRIPE_SUBSCRIPTION_PERIOD, {
    variables: { userId, previousMonths },
    notifyOnNetworkStatusChange: true
  });

  const stripeSubscription = data?.user.stripeSubscription;
  const subscriptionPeriod = stripeSubscription?.subscriptionPeriod;

  if (!loading && subscriptionPeriod && stripeSubscription?.productName) {
    lastDisplayedRef.current = {
      productName: stripeSubscription.productName,
      period: subscriptionPeriod
    };
  }

  const displayed = lastDisplayedRef.current;
  const productName = displayed?.productName;
  const { limit, used, percentageUsed } = displayed?.period ?? {};
  const progressPercent = Math.min(100, Math.max(0, percentageUsed ?? 0));
  const isViewingPastPeriod = previousMonths > 0;
  const isLoadingPeriod = loading;

  const resetPeriodToNow = () => setPreviousMonths(0);

  const periodStart = isLoadingPeriod ? undefined : subscriptionPeriod?.start;
  const periodEnd = isLoadingPeriod ? undefined : subscriptionPeriod?.end;

  const periodRangeLabel =
    periodStart && periodEnd
      ? translate('period-range', {
          start: formatPeriodDate(periodStart),
          end: formatPeriodDate(periodEnd)
        })
      : null;

  const requestsLabel = translate('requests');

  if (!displayed) {
    return null;
  }

  return (
    <div className="mb-4 mt-4 flex flex-col gap-4 rounded-lg border border-base-300 bg-base-100 p-4">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          className="flex size-8 shrink-0 items-center justify-center rounded-full text-primary hover:bg-base-200"
          onClick={() => setPreviousMonths((months) => months + 1)}
        >
          <Icon icon="chevron-left" />
        </button>

        {isViewingPastPeriod ? (
          <button
            type="button"
            className="flex min-h-8 min-w-0 flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg px-2 text-primary transition-colors hover:bg-base-200 hover:underline"
            onClick={resetPeriodToNow}
          >
            <span className="text-center text-sm">{isLoadingPeriod ? <Icon loading /> : periodRangeLabel}</span>
            <Icon icon="rotate-left" className="shrink-0" />
          </button>
        ) : (
          <div className="flex min-h-5 min-w-0 flex-1 items-center justify-center gap-2">
            <span className="text-center text-sm">
              {isLoadingPeriod ? <Icon loading className="text-primary" /> : periodRangeLabel}
            </span>
          </div>
        )}

        <button
          type="button"
          className="flex size-8 shrink-0 items-center justify-center rounded-full text-primary hover:bg-base-200 disabled:cursor-not-allowed disabled:opacity-40"
          onClick={() => setPreviousMonths((months) => Math.max(0, months - 1))}
          disabled={!isViewingPastPeriod}
        >
          <Icon icon="chevron-right" />
        </button>
      </div>

      <div className={`flex flex-col gap-4${isLoadingPeriod ? 'opacity-60' : ''}`}>
        <div className="flex items-center justify-between gap-4 text-sm">
          <span>{productName}</span>
          <span>
            <span className="font-bold">{used ?? 0}</span>
            {limit != null ? (
              <>
                {` / ${limit} ${requestsLabel}`}
                {percentageUsed != null && ` (${Math.round(percentageUsed)}%)`}
              </>
            ) : (
              ` / ∞ ${requestsLabel}`
            )}
          </span>
        </div>

        {limit != null && (
          <div className="h-2 w-full overflow-hidden rounded-full bg-primary-100">
            <div
              className="h-full rounded-full bg-primary transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentRequestUsage;
